import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import approvalService from '../../services/approvalService';
import projectService from '../../services/projectService';
import userService from '../../services/userService';
import './ApprovalCreate.css';

const ApprovalCreate = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    approval_type: 'Other',
    priority: 'Medium',
    project_id: '',
    approver_id: '',
    requested_amount: '',
    due_date: '',
    is_urgent: false
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getAllProjects()
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll()
  });

  const projects = projectsData?.projects || [];
  const users = usersData?.users || [];

  const createMutation = useMutation({
    mutationFn: approvalService.createApproval,
    onSuccess: () => {
      queryClient.invalidateQueries(['approvals']);
      toast.success('Approval request created successfully');
      navigate('/app/approvals');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create approval request');
    }
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.approver_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    const submitData = {
      ...formData,
      requested_amount: formData.requested_amount ? parseFloat(formData.requested_amount) : null,
      project_id: formData.project_id || null,
      due_date: formData.due_date || null
    };

    createMutation.mutate(submitData);
  };

  return (
    <div className="approval-create-page">
      <div className="page-header">
        <h1>Create Approval Request</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/app/approvals')}>
          <i className="fas fa-arrow-left"></i> Back to List
        </button>
      </div>

      <form onSubmit={handleSubmit} className="approval-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Enter approval title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Enter detailed description"
            />
          </div>

          <div className="form-row custom-request">
            <div className="form-group">
              <label htmlFor="approval_type">Approval Type *</label>
              <select
                id="approval_type"
                name="approval_type"
                value={formData.approval_type}
                onChange={handleChange}
                required
              >
                <option value="Budget">Budget</option>
                <option value="Change Request">Change Request</option>
                <option value="Document">Document</option>
                <option value="Expense">Expense</option>
                <option value="Project">Project</option>
                <option value="Resource">Resource</option>
                <option value="Risk">Risk</option>
                <option value="Task">Task</option>
                <option value="Time Off">Time Off</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="priority">Priority *</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                required
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="form-row custom-request">
            <div className="form-group">
              <label htmlFor="project_id">Project</label>
              <select
                id="project_id"
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
              >
                <option value="">Select Project (Optional)</option>
                {projects?.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="approver_id">Approver *</label>
              <select
                id="approver_id"
                name="approver_id"
                value={formData.approver_id}
                onChange={handleChange}
                required
              >
                <option value="">Select Approver</option>
                {users?.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row custom-request">
            <div className="form-group">
              <label htmlFor="requested_amount">Requested Amount</label>
              <input
                type="number"
                id="requested_amount"
                name="requested_amount"
                value={formData.requested_amount}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="due_date">Due Date</label>
              <input
                type="date"
                id="due_date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="is_urgent"
                checked={formData.is_urgent}
                onChange={handleChange}
              />
              <span>Mark as Urgent</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/app/approvals')}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={createMutation.isLoading}>
            {createMutation.isLoading ? 'Creating...' : 'Create Approval Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApprovalCreate;


